/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import FocusTrap from 'focus-trap-react';

import styles from './Modal.module.scss';
import { ArrowLeft, CloseS1 } from '../Icons';
import IconButton from '../IconButton';
import { Modal as DeprecatedModal } from '../deprecated/Modal';
import OneColumnLayout from './layouts/OneColumnLayout';
import TwoColumnLayout from './layouts/TwoColumnLayout';
import { ViewportSizeContext } from './ViewportSizeContext';
import { Props, MultistepModalProps } from './shared-types';
import { Props as DeprecatedModalProps } from '../deprecated/Modal';

import useBreakpoints from '../utils/hooks/useBreakpoints';
import useLockedBody from '../utils/hooks/useLockedBody';
import isTestRender from '../utils/isTestRender';

type CombinedModalProps = (Props & MultistepModalProps) | DeprecatedModalProps;

export const Modal: React.FC<CombinedModalProps> = ({
  variant = 'default',
  maxModalWidth = 'mobile',
  contentColumns,
  modalRef,
  disclaimer,
  heading,
  modalState,
  onStateChange,
  showBackButton = false,
  progress,
  onBackClick = () => {},
  onClose,
  footerButtons,
  children,
  className,
  useV2 = false,
  ...restProps
}: Props & MultistepModalProps) => {
  const { isSmall, isMedium, isLarge, isXL } = useBreakpoints();

  const [locked, setLocked] = useState(false);

  const [viewportSize, setViewportSize] = useState({
    isSmall,
    isMedium,
    isLarge,
    isXL,
  });

  const [isInitialFocus, setIsInitialFocus] = useState(true);

  const handleKeyDown = (e: React.KeyboardEvent | KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setLocked(false);
        onStateChange('closing');
        setIsInitialFocus(true);
        break;
      case 'Tab':
        if (isInitialFocus) {
          setIsInitialFocus(false);
        }
        break;
    }
    return true;
  };

  const handleMouseMove = () => {
    if (isInitialFocus) {
      setIsInitialFocus(false);
    }
  };

  useEffect(() => {
    setViewportSize(() => ({ isSmall, isMedium, isLarge, isXL }));
  }, [isSmall, isMedium, isLarge, isXL]);

  useEffect(() => {
    const modalEl = modalRef?.current;
    modalEl?.addEventListener('keydown', handleKeyDown);
    modalEl?.addEventListener('mousemove', handleMouseMove);

    return () => {
      modalEl?.removeEventListener('keydown', handleKeyDown);
      modalEl?.removeEventListener('mousemove', handleMouseMove);
    };
  });

  useLockedBody(locked);

  const useOneColumnLayout =
    maxModalWidth !== 'desktop-lg' ||
    (maxModalWidth === 'desktop-lg' && !viewportSize.isXL) ||
    contentColumns.length === 1;

  const isModalOpen = modalState === 'open';
  const isModalClosing = modalState === 'closing';
  const isModalClosed = modalState === 'closed';

  useEffect(() => {
    if (isModalOpen && modalRef.current) {
      setIsInitialFocus(true);
      modalRef.current?.showModal();
      // Fix tooltip positioning error
      window.dispatchEvent(new Event('resize'));
    }
    return () => modalRef.current?.close();
  }, [modalState, isModalOpen]);

  const handleAnimationStart = () => {
    if (isModalClosing || isModalClosed) return;
    setLocked(true);
  };

  const handleAnimationEnd = () => {
    if (!isModalClosing) return;
    onClose();
    onStateChange('closing');
    setLocked(false);
    onStateChange('closed');
    modalRef.current?.close();
    setIsInitialFocus(true);
  };

  const handleClose = () => {
    if (isModalOpen) {
      setLocked(false);
      onStateChange('closing');
    }
  };

  const hasProgressed = progress && progress > 0;

  return useV2 ? (
    <ViewportSizeContext.Provider value={viewportSize}>
      <FocusTrap active={locked}>
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
        <dialog
          aria-labelledby='modalHeading'
          aria-modal={['dialog', 'multistep'].includes(variant)}
          role={variant === 'default' ? 'dialog' : ''}
          ref={modalRef}
          className={cx(
            styles.modal,
            styles[variant],
            styles[maxModalWidth],
            isModalClosing && styles.closing,
            isTestRender() && styles.testRender,
            className,
          )}
          onAnimationStart={handleAnimationStart}
          onAnimationEnd={handleAnimationEnd}
          onKeyDown={handleKeyDown}
        >
          <div className={styles.titlebarContainer}>
            <div className={styles.titlebar}>
              {variant === 'multistep' && (
                <>
                  <div className={styles.iconButtonContainer}>
                    {showBackButton && hasProgressed ? (
                      <div className={cx(styles.backButton)}>
                        <IconButton
                          accessibilityLabel={'Previous step'}
                          icon={<ArrowLeft />}
                          onClick={onBackClick}
                          tabIndex={0}
                          hideTooltip={isInitialFocus}
                        />
                      </div>
                    ) : null}
                  </div>
                  <div
                    role='heading'
                    aria-level={2}
                    id='modalHeading'
                    className={styles.modalHeading}
                  >
                    {heading}
                  </div>
                </>
              )}
              {variant !== 'dialog' && (
                <div className={styles.closeButton}>
                  <div className={styles.iconButtonContainer}>
                    <IconButton
                      accessibilityLabel={'Close'}
                      icon={<CloseS1 />}
                      onClick={handleClose}
                      tabIndex={0}
                      hideTooltip={isInitialFocus}
                    />
                  </div>
                </div>
              )}
            </div>
            {variant === 'multistep' && (
              <div className={styles.progressbar}>
                <div
                  className={styles.progress}
                  style={{ width: `${progress}%` }}
                  aria-label={hasProgressed ? `Progress: ${progress}%` : ''}
                  aria-live='assertive'
                ></div>
              </div>
            )}
          </div>
          <div className={styles.body}>
            {useOneColumnLayout ? (
              <OneColumnLayout
                {...{
                  contentColumns,
                  disclaimer,
                  footerButtons,
                  maxModalWidth,
                  viewportSize,
                }}
              />
            ) : (
              <TwoColumnLayout
                {...{
                  contentColumns,
                  disclaimer,
                  footerButtons,
                  maxModalWidth,
                  viewportSize,
                }}
              />
            )}
          </div>
        </dialog>
      </FocusTrap>
    </ViewportSizeContext.Provider>
  ) : (
    <DeprecatedModal {...restProps} className={className}>
      {children}
    </DeprecatedModal>
  );
};

Modal.displayName = 'Modal';
export default Modal;
export { styles };
